import { AxiosRequestConfig } from "axios";
import { CohesiveAPIClient } from "../../client";
import {
  CreateFlowMessageAnnotationPayload,
  CreateFlowMessageAnnotationResponse,
  CreateFlowSessionAnnotationPayload,
  CreateFlowSessionAnnotationResponse,
  GetAnnotationStatsParams,
  GetAnnotationStatsResponse,
  GetPredefinedTagsResponse,
  ListFlowSessionsParams,
  ListFlowSessionsResponse,
  ShowFlowSessionResponse,
} from "./types";

export * from "./types";

const ENDPOINTS = {
  LIST_FLOW_SESSIONS: "/api/v2/admin/flow-sessions",
  SHOW_FLOW_SESSION: "/api/v2/admin/flow-sessions/:flow_session_id",
  CREATE_SESSION_ANNOTATION:
    "/api/v2/admin/flow-sessions/:flow_session_id/annotations",
  CREATE_MESSAGE_ANNOTATION:
    "/api/v2/admin/flow-messages/:flow_message_id/annotations",
  DELETE_ANNOTATION: "/api/v2/admin/annotations/:annotation_id",
  GET_STATS: "/api/v2/admin/annotations/stats",
  GET_PREDEFINED_TAGS: "/api/v2/admin/annotations/tags",
};

export class FlowSessionAnnotationsAPI {
  constructor(private client: CohesiveAPIClient) {}

  async listFlowSessions(
    params: ListFlowSessionsParams = {},
    config?: AxiosRequestConfig,
  ): Promise<ListFlowSessionsResponse> {
    const searchParams = new URLSearchParams();

    if (params.organization_id)
      searchParams.append("organization_id", params.organization_id);
    if (params.user_id) searchParams.append("user_id", params.user_id);
    if (params.status) searchParams.append("status", params.status);
    if (params.before_ts) searchParams.append("before_ts", params.before_ts);
    if (params.after_ts) searchParams.append("after_ts", params.after_ts);
    if (params.type) searchParams.append("type", params.type);
    if (params.has_annotations !== undefined)
      searchParams.append("has_annotations", String(params.has_annotations));
    if (params.annotation_rating)
      searchParams.append("annotation_rating", params.annotation_rating);
    if (params.annotation_tags) {
      params.annotation_tags.forEach((tag) =>
        searchParams.append("annotation_tags", tag),
      );
    }
    if (params.paying_orgs_only !== undefined)
      searchParams.append("paying_orgs_only", String(params.paying_orgs_only));
    if (params.limit) searchParams.append("limit", String(params.limit));
    if (params.offset) searchParams.append("offset", String(params.offset));

    const url = `${ENDPOINTS.LIST_FLOW_SESSIONS}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    const response = await this.client.axios.get<ListFlowSessionsResponse>(
      url,
      config,
    );
    return response.data;
  }

  async getFlowSessionDetail(
    flowSessionId: string,
    config?: AxiosRequestConfig,
  ): Promise<ShowFlowSessionResponse> {
    const url = ENDPOINTS.SHOW_FLOW_SESSION.replace(
      ":flow_session_id",
      flowSessionId,
    );
    const response = await this.client.axios.get<ShowFlowSessionResponse>(
      url,
      config,
    );
    return response.data;
  }

  async getAnnotationStats(
    params: GetAnnotationStatsParams = {},
    config?: AxiosRequestConfig,
  ): Promise<GetAnnotationStatsResponse> {
    const searchParams = new URLSearchParams();

    if (params.organization_id)
      searchParams.append("organization_id", params.organization_id);
    if (params.after_ts) searchParams.append("after_ts", params.after_ts);
    if (params.before_ts) searchParams.append("before_ts", params.before_ts);

    const url = `${ENDPOINTS.GET_STATS}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    const response = await this.client.axios.get<GetAnnotationStatsResponse>(
      url,
      config,
    );
    return response.data;
  }

  async createSessionAnnotation(
    flowSessionId: string,
    payload: CreateFlowSessionAnnotationPayload,
    config?: AxiosRequestConfig,
  ): Promise<CreateFlowSessionAnnotationResponse> {
    const url = ENDPOINTS.CREATE_SESSION_ANNOTATION.replace(
      ":flow_session_id",
      flowSessionId,
    );
    const response =
      await this.client.axios.post<CreateFlowSessionAnnotationResponse>(
        url,
        payload,
        config,
      );
    return response.data;
  }

  async createMessageAnnotation(
    flowMessageId: string,
    payload: CreateFlowMessageAnnotationPayload,
    config?: AxiosRequestConfig,
  ): Promise<CreateFlowMessageAnnotationResponse> {
    const url = ENDPOINTS.CREATE_MESSAGE_ANNOTATION.replace(
      ":flow_message_id",
      flowMessageId,
    );
    const response =
      await this.client.axios.post<CreateFlowMessageAnnotationResponse>(
        url,
        payload,
        config,
      );
    return response.data;
  }

  async deleteAnnotation(
    annotationId: string,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    const url = ENDPOINTS.DELETE_ANNOTATION.replace(
      ":annotation_id",
      annotationId,
    );
    await this.client.axios.delete(url, config);
  }

  async getPredefinedTags(
    config?: AxiosRequestConfig,
  ): Promise<GetPredefinedTagsResponse> {
    const response = await this.client.axios.get<GetPredefinedTagsResponse>(
      ENDPOINTS.GET_PREDEFINED_TAGS,
      config,
    );
    return response.data;
  }
}
